import { useRef, useState, type ReactNode } from 'react';
import {
  Button,
  HeadingText,
  StackLayout,
  Box,
  Checkbox,
  FormField,
  TextAction,
  type GDSCheckboxProps,
} from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import { useSelector } from '@xstate/react';
import { OnboardingCard } from 'components/onboarding/onboarding-card/onboarding-card.component';
import { useIsOnboardingMobile } from 'hooks/use-is-onboarding-mobile/use-is-onboarding-mobile.hook';
import { OnboardingMachineContext } from 'components/onboarding/onboarding.machine';
import { EXTERNAL_LINKS } from 'common/links';
import termsAndConditionsIllustration from 'assets/account-terms-clipboard.svg';

const TERMS_CHECKBOX_ID = 'tc-agreement';
const MARKETING_CHECKBOX_ID = 'tc-marketing-opt-in';
// Shared by the FormField and the Checkbox it wraps. GDSCheckboxProps requires
// id/name on the input even though FormField clones its own onto the child and
// those win, so both copies have to exist — routing them through one constant
// keeps them from drifting into a silently ignored value.
const TERMS_CHECKBOX_NAME = 'termsAccepted';

// TextAction inside the Checkbox's label would otherwise bubble its click up
// to the surrounding <label> and toggle the checkbox in addition to
// navigating. stopPropagation keeps the link click scoped to the link.
const stopLabelClickBubble = (event: React.MouseEvent) =>
  event.stopPropagation();

// Rich-text chunk renderers for the links embedded in the agreement
// sentence (STR_TERMS_AND_CONDITIONS_AGREEMENT's <privacy>/<terms> tags).
// Module scope keeps them stable references — same pattern as
// mailing-address-section's renderSupportLink (avoids
// react/no-unstable-nested-components).
//
// TextAction's own "medium" typography is $buttonTwo (14px/bold/no
// underline, a CTA style) — the Figma spec instead wants these inline as a
// plain hyperlink: same weight/size as the surrounding sentence ($bodyOne)
// and underlined at rest (TextAction only underlines on hover by default).
// Both are Genesis typography tokens, not raw px/hardcoded weights.
const inlineLinkCss = {
  typography: '$bodyOne',
  textDecoration: 'underline',
} as const;

const renderPrivacyPolicyLink = (chunks: ReactNode) => (
  <TextAction
    href={EXTERNAL_LINKS.PRIVACY}
    target="_blank"
    rel="noopener noreferrer"
    onClick={stopLabelClickBubble}
    css={inlineLinkCss}
  >
    {chunks}
  </TextAction>
);

const renderTermsLink = (chunks: ReactNode) => (
  <TextAction
    href={EXTERNAL_LINKS.TERMS}
    target="_blank"
    rel="noopener noreferrer"
    onClick={stopLabelClickBubble}
    css={inlineLinkCss}
  >
    {chunks}
  </TextAction>
);

export const TermsAndConditionsView = () => {
  const { formatMessage } = useIntl();
  const actor = OnboardingMachineContext.useActorRef();
  const { send } = actor;
  const hasEmail = useSelector(
    actor,
    (snapshot) => snapshot.context.email.trim() !== '',
  );
  const isOnboardingMobile = useIsOnboardingMobile();

  const [termsAccepted, setTermsAccepted] = useState(false);
  const [marketingEmailsOptIn, setMarketingEmailsOptIn] = useState(false);
  // Continue is always enabled (no disabled-button gate); this flag drives
  // the inline error instead. Set on a Continue click while unchecked, and
  // cleared as soon as the member checks the box (see termsCheckboxProps'
  // onChange below) — mirrors the iOS `showsTermsAcceptanceError` flag.
  const [showsTermsAcceptanceError, setShowsTermsAcceptanceError] =
    useState(false);
  const termsCheckboxRef = useRef<HTMLInputElement>(null);

  const handleContinue = () => {
    if (!termsAccepted) {
      setShowsTermsAcceptanceError(true);
      // Send focus to the input that blocked submission. The status message
      // sits above the button, so without this a keyboard or screen-reader
      // user is left on Continue with the explanation behind them. Landing on
      // the checkbox also re-announces the error (its aria-describedby points
      // at the message) on every repeat click, which the aria-live region
      // alone would not do once the message text stops changing.
      termsCheckboxRef.current?.focus();
      return;
    }

    send({
      type: 'navigateToNextView',
      payload: { marketingEmailsOptIn: hasEmail && marketingEmailsOptIn },
    });
  };

  // The agreement sentence is ONE translatable message with <privacy>/<terms>
  // rich-text tags so translators own the full word order — no fragment
  // concatenation. formatMessage returns an ARRAY here (not a ReactElement),
  // so Genesis's isValidElement(label) branch is false and the label renders
  // through LabelText with the standard GDS-checkbox-label-text typography.
  // aria-label provides the plain-text fallback for AT that may not flatten
  // the rich label.
  const termsCheckboxProps = {
    id: TERMS_CHECKBOX_ID,
    name: TERMS_CHECKBOX_NAME,
    checked: termsAccepted,
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
      const { checked } = event.target;
      setTermsAccepted(checked);
      if (checked) {
        setShowsTermsAcceptanceError(false);
      }
    },
    label: formatMessage(
      { id: 'STR_TERMS_AND_CONDITIONS_AGREEMENT' },
      {
        privacy: renderPrivacyPolicyLink,
        terms: renderTermsLink,
      },
    ),
    'aria-label': formatMessage({
      id: 'STR_TERMS_AND_CONDITIONS_AGREEMENT_ARIA_LABEL',
    }),
    required: true,
  };

  const marketingCheckboxProps = {
    id: MARKETING_CHECKBOX_ID,
    name: 'marketingEmailsOptIn',
    checked: marketingEmailsOptIn,
    onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
      setMarketingEmailsOptIn(event.target.checked),
    label: formatMessage({ id: 'STR_TERMS_AND_CONDITIONS_MARKETING_OPT_IN' }),
  };

  return (
    <OnboardingCard>
      <StackLayout
        spacing="$twoAndHalf"
        // VerticalStackLayout defaults horizontalAlignment to "start", so
        // without an explicit "stretch" here its children (this section
        // and the checkbox section below) hug their own content width and
        // sit flush-left instead of spanning the card. That left the
        // heading+illustration sub-stack's own horizontalAlignment=
        // "center" with no slack to center within (its box was already as
        // tight as its widest child, the heading) — pre-existing from the
        // original T&C screen, not introduced by the error-state work.
        // Stretching here gives both sub-stacks the full card width, so
        // the heading section's own "center" actually centers.
        horizontalAlignment="stretch"
        css={{
          paddingBlockStart: '$twoAndHalf',
          paddingInline: '$twoAndHalf',
        }}
      >
        <StackLayout spacing="$oneAndHalf" horizontalAlignment="center">
          <HeadingText
            size="xl"
            level="2"
            as="h1"
            css={{ textAlign: 'center' }}
          >
            {formatMessage({ id: 'STR_TERMS_AND_CONDITIONS_HEADING' })}
          </HeadingText>
          <Box
            as="img"
            src={termsAndConditionsIllustration}
            alt=""
            // HTML width/height attributes reserve intrinsic dimensions so
            // the browser can prevent layout shift while the asset loads.
            width={160}
            height={160}
            css={{ display: 'block' }}
          />
        </StackLayout>

        <StackLayout spacing="$one" horizontalAlignment="stretch">
          {/*
              Genesis's published .d.ts (compiled against a newer
              @types/react) bakes `exportparts`/`part` into the Checkbox
              prop Pick; our pinned @types/react@18.2.42 predates those DOM
              attributes, so src/types/react-html-attributes.d.ts declares
              them as optional augmentations to keep Genesis inputs
              compiling. The GDSCheckboxProps casts stay per the repo
              convention: cast Genesis prop bags to the component's own GDS
              type, never `any`.
            */}
          {/*
              FormField clones id/name/aria-describedby/inputStatus onto its
              DIRECT child, so the Checkbox must stay the only child (no
              wrapping Fragment — see the genesis-formfield-clone-direct-child
              gotcha). `label` is left undefined because the Checkbox already
              renders its own accessible label (with the inline Privacy Policy
              / Terms & Conditions links); a field-level label would duplicate
              the accessible name.

              The status-message overrides both compensate for the $half (8px)
              touch-target padding on the Checkbox's own <label>, which the
              plain FormField status row doesn't share: marginInlineStart
              re-indents the row to line up with the checkbox glyph, and
              marginBlockStart nets Genesis's own $quarter back down to the
              4px gap in the Figma spec once that 8px inset is subtracted.
            */}
          <FormField
            id={TERMS_CHECKBOX_ID}
            name={TERMS_CHECKBOX_NAME}
            label={undefined}
            inputStatus={showsTermsAcceptanceError ? 'error' : undefined}
            statusIconLabel={formatMessage({ id: 'STR_ERROR' })}
            statusMessage={
              showsTermsAcceptanceError
                ? formatMessage({
                    id: 'STR_TERMS_AND_CONDITIONS_ACCEPTANCE_ERROR',
                  })
                : undefined
            }
            css={{
              '.GDS-input-status-message-wrapper': {
                marginInlineStart: '$half',
                marginBlockStart: 'calc($quarter - $half)',
              },
            }}
          >
            <Checkbox
              {...(termsCheckboxProps as GDSCheckboxProps)}
              ref={termsCheckboxRef}
            />
          </FormField>
          {hasEmail ? (
            <Checkbox {...(marketingCheckboxProps as GDSCheckboxProps)} />
          ) : null}
        </StackLayout>
      </StackLayout>

      <Box
        css={{
          borderColor: '$onSurfaceBorderDefault',
          borderStyle: 'solid',
          borderBlockStartWidth: 'thin',
          marginBlockStart: '$twoAndHalf',
          padding: '$one',
          // Figma pads this row differently per breakpoint: desktop only
          // insets the (right-aligned) button from the right (pr-32);
          // mobile insets both sides evenly (px-24) since the button
          // fills the row there.
          paddingInline: isOnboardingMobile ? '$oneAndHalf' : '$two',
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <Button
          type="button"
          size="large"
          priority="primary"
          onClick={handleContinue}
          width={isOnboardingMobile ? 'fillContainer' : 'hugContents'}
        >
          {formatMessage({ id: 'STR_CONTINUE' })}
        </Button>
      </Box>
    </OnboardingCard>
  );
};
